import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useTagStore } from '@/manage/stores/tag'
import { userApi } from '@/manage/api/user'
import type { UserTag } from '@/manage/types/user'
import { notifyError, notifySuccess, notifyWarning } from '@/utils/error'
import { usePostHog } from '@/shared/composables/usePostHog'

type Translate = (key: string, named?: Record<string, unknown>) => string

const isDefaultTag = (tag: UserTag | null | undefined) => Boolean(tag?.is_default)

export const useSidebarTags = ({ t }: { t: Translate }) => {
  const route = useRoute()
  const tagStore = useTagStore()
  const { posthog, hasPostHogKey } = usePostHog()
  const tagSearchKeyword = ref('')

  const filteredTags = computed(() => tagStore.tags)
  const allCandidatesCount = computed(() => {
    if (typeof tagStore.totalCandidateCount === 'number') {
      return tagStore.totalCandidateCount
    }
    return tagStore.tags.reduce((sum, tag) => sum + tag.candidate_count, 0)
  })
  const totalTagsCount = computed(() => {
    if (typeof tagStore.totalTagCount === 'number') {
      return tagStore.totalTagCount
    }
    return filteredTags.value.length
  })
  const filteredTagsSignature = computed(() =>
    filteredTags.value.map((tag) => String(tag.id)).join('|'),
  )
  const tagLoading = computed(() => tagStore.loading)
  const hasMoreTags = computed(() => tagStore.hasMore)

  let searchTimer: number | null = null
  let lastTagSearchQuery: string | null = null

  onMounted(() => {
    void tagStore.ensureDefaultTagsLoaded()
  })

  onUnmounted(() => {
    if (searchTimer) {
      window.clearTimeout(searchTimer)
      searchTimer = null
    }
  })

  watch(
    () => tagSearchKeyword.value,
    (val) => {
      if (searchTimer) window.clearTimeout(searchTimer)
      const normalized = String(val || '').trim()
      const nextQuery = normalized || null
      searchTimer = window.setTimeout(() => {
        if (nextQuery === lastTagSearchQuery) return
        lastTagSearchQuery = nextQuery
        void tagStore.fetchTags({ reset: true, query: nextQuery })
      }, 300)
    },
  )

  const fetchMoreTags = () => tagStore.fetchTags()

  const handleTagClick = (tag: UserTag) => {
    if (hasPostHogKey) {
      posthog.capture('tag_selected', {
        source: 'sidebar_tag_menu',
        tag_id: String(tag.id),
        tag_name: tag.name,
        candidate_count: Number(tag.candidate_count || 0),
        is_vip_tag: Boolean(tag.vip_tag),
        is_default_tag: Boolean(tag.is_default),
      })
    }
    if (route.path === '/manage/talent-pool' && route.query.tag === String(tag.id)) {
      tagStore.triggerRefresh()
    }
  }

  const pinTag = async (tag: UserTag) => {
    if (isDefaultTag(tag)) {
      notifyWarning(t('talent.defaultGroupLocked'))
      return
    }
    await userApi.updateUserTag(tag.id, { is_top: !tag.is_top })
    if (hasPostHogKey) {
      posthog.capture('tag_pin_toggled', {
        source: 'sidebar_context_menu',
        tag_id: String(tag.id),
        tag_name: tag.name,
        is_top: !tag.is_top,
      })
    }
    tagStore.patchTagLocal(tag.id, { is_top: !tag.is_top })
    notifySuccess(t('header.updateSuccess'))
  }

  const renameTag = async (tag: UserTag, name: string) => {
    if (isDefaultTag(tag)) {
      notifyWarning(t('talent.defaultGroupLocked'))
      return
    }
    await userApi.updateUserTag(tag.id, { name })
    if (hasPostHogKey) {
      posthog.capture('tag_renamed', {
        source: 'sidebar_rename_dialog',
        tag_id: String(tag.id),
        previous_name: tag.name,
        tag_name: name.trim(),
      })
    }
    tagStore.patchTagLocal(tag.id, { name: name.trim() })
    notifySuccess(t('header.updateSuccess'))
  }

  const deleteTag = async (tag: UserTag) => {
    if (isDefaultTag(tag)) {
      notifyWarning(t('talent.defaultGroupLocked'))
      return
    }
    await userApi.deleteUserTag(tag.id)
    if (hasPostHogKey) {
      posthog.capture('tag_deleted', {
        source: 'sidebar_delete_dialog',
        tag_id: String(tag.id),
        tag_name: tag.name,
      })
    }
    tagStore.removeTagLocal(tag.id)
    notifySuccess(t('header.deleteSuccess'))
  }

  const notifyTagError = (scope: string, error: unknown) => {
    notifyError({ scope, error, message: t('error.requestFailed') })
  }

  return {
    tagSearchKeyword,
    filteredTags,
    allCandidatesCount,
    totalTagsCount,
    filteredTagsSignature,
    tagLoading,
    hasMoreTags,
    fetchMoreTags,
    handleTagClick,
    pinTag,
    renameTag,
    deleteTag,
    notifyTagError,
  }
}
